/**
 * <s-shop-scene> keeps the shop in step with the bag. After every cart update
 * it mirrors the cart drawer's total into [data-register-total] with a small
 * "ka-ching" bump, writes the item count into [data-cart-count] (showing its
 * [data-cart-count-badge] only when the bag has something in it), and sets
 * `data-has-items` on itself so the total can join "Check out here". It also
 * keeps every panel Add to bag button in step with the real bag: "Added" while
 * that candle is in the bag, back to "Add to bag" once it is removed.
 */
class SShopScene extends HTMLElement {
  #observer;
  #debounce;
  #ac;

  connectedCallback() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    this.#observer = new MutationObserver(() => {
      this.#syncTotal();
      clearTimeout(this.#debounce);
      this.#debounce = setTimeout(() => this.#syncCount(), 150);
    });
    this.#observer.observe(drawer, { childList: true, subtree: true, characterData: true });
    this.#syncCount();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#debounce);
    this.#ac?.abort();
  }

  async #syncCount() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: this.#ac.signal });
      if (!response.ok) return;
      const { item_count: count = 0, items = [] } = await response.json();
      // Panel buttons read "Added" exactly while their candle is in the bag, however it got there or left.
      const inBag = new Set(items.map((item) => String(item.variant_id)));
      document.querySelectorAll("s-product-form[data-stay][data-variant-id]").forEach((form) => {
        form.toggleAttribute("data-added", inBag.has(form.dataset.variantId));
      });
      this.toggleAttribute('data-has-items', count > 0);
      this.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = String(count); });
      this.querySelectorAll('[data-cart-count-badge]').forEach((el) => { el.hidden = count === 0; });
    } catch {
      // A newer update is already on its way, or the network is down; the badge keeps its last value.
    }
  }

  #syncTotal() {
    const source = document.querySelector(this.dataset.cartTotalSource || '[data-cart-total]');
    const display = this.querySelector('[data-register-total]');
    if (!display) return;
    const next = source ? source.textContent.trim() : display.dataset.emptyTotal || '';
    if (!next || next === display.textContent.trim()) return;
    display.textContent = next;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      display.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }],
        { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }
  }
}

if (!customElements.get('s-shop-scene')) customElements.define('s-shop-scene', SShopScene);
